import { Icon } from "@/components/ui/display/Icon";
import { IconButton } from "@/components/ui/buttons/IconButton";
import { ChipGroup } from "@/components/ui/display/ChipGroup";
import classNames from "classnames";
import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./Select.module.css";
import type { SelectOption, SelectProps } from "./types";

/**
 * Select component - A searchable dropdown with single/multi-select support.
 * Matches the Vue Dropdown.vue component exactly in appearance and functionality.
 *
 * WCAG AAA Compliant:
 * - Proper combobox ARIA pattern
 * - Keyboard navigation (Arrow keys, Enter, Escape)
 * - Screen reader announcements for selections
 * - 44px minimum touch targets
 * - 7:1 color contrast ratios
 */
export const Select = <T,>({
  id,
  label,
  placeholder = "Select...",
  options,
  value = [],
  multiple = false,
  searchable = true,
  sorted = true,
  dark = false,
  appearance = "default",
  allowAddNew = false,
  disabled = false,
  error,
  size = "md",
  chipGroupMax,
  chipGroupTitle,
  onChange,
  onAddNew,
  className,
  "data-testid": testId,
  renderOption,
  renderValue,
}: SelectProps<T>): React.ReactElement => {
  const isClean = appearance === "clean";
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [keyboardIndex, setKeyboardIndex] = useState<number | null>(null);
  const generatedId = useId();
  const fieldId = id || testId || `select-${generatedId}`;
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Track if blur was caused by scroll to prevent unwanted close
  const blurFromScrollRef = useRef(false);

  // Sort options alphabetically if sorted prop is true
  const sortedItems = useMemo(() => {
    if (!sorted) return options;
    return [...options].sort((a, b) => {
      const textA = a.label.toUpperCase();
      const textB = b.label.toUpperCase();
      return textA < textB ? -1 : textA > textB ? 1 : 0;
    });
  }, [options, sorted]);

  // Filter items based on search query (AND logic for multi-word queries)
  const filteredItems = useMemo(() => {
    const selectedIds = value.map((v) => v.id);

    return sortedItems.filter((item) => {
      // In multi-select mode, hide already selected items
      if (multiple && selectedIds.includes(item.id)) return false;

      // Match each word (AND logic)
      if (!searchQuery) return true;
      const queryStrings = searchQuery.toLowerCase().split(" ").filter(Boolean);
      return queryStrings.every((query) =>
        item.label.toLowerCase().includes(query),
      );
    });
  }, [sortedItems, value, searchQuery, multiple]);

  // Get visible item indices for keyboard navigation
  const visibleIndices = useMemo(() => {
    return filteredItems.map((item) => sortedItems.indexOf(item));
  }, [filteredItems, sortedItems]);

  // Display text for the input
  const displayText = useMemo(() => {
    if (open) return searchQuery;
    if (!multiple && value.length > 0) return value[0].label;
    return "";
  }, [open, searchQuery, multiple, value]);

  // Handle input focus
  const handleFocus = useCallback(() => {
    if (disabled) return;
    setOpen(true);
    setKeyboardIndex(null);
    if (!multiple && value.length > 0) {
      // Clear search to show all options when opening
      setSearchQuery("");
    }
  }, [disabled, multiple, value]);

  // Handle input blur
  const handleBlur = useCallback(() => {
    // If blur was caused by scroll, re-focus the input and don't close
    if (blurFromScrollRef.current) {
      blurFromScrollRef.current = false;
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return;
    }

    // Delay to allow click events on items to fire first
    setTimeout(() => {
      setOpen(false);
      setKeyboardIndex(null);
      if (!multiple && value.length > 0) {
        setSearchQuery(value[0].label);
      } else {
        setSearchQuery("");
      }
    }, 150);
  }, [multiple, value]);

  // Track scroll events when dropdown is open to prevent close on scroll
  useEffect(() => {
    if (!open) return;

    const handleScroll = () => {
      blurFromScrollRef.current = true;
      // Reset after a short delay
      setTimeout(() => {
        blurFromScrollRef.current = false;
      }, 200);
    };

    // Use capture phase to catch scroll before blur
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [open]);

  // Handle selecting an option
  const handleSelect = useCallback(
    (item: SelectOption<T>) => {
      if (item.disabled) return;

      let newSelection: SelectOption<T>[];

      if (multiple) {
        // Add to selection
        newSelection = [...value, item];
        setSearchQuery("");
      } else {
        newSelection = [item];
        setSearchQuery(item.label);
        inputRef.current?.blur();
      }

      onChange?.(newSelection);
    },
    [multiple, value, onChange],
  );

  // Handle removing a selected item (multi-select chips)
  const handleRemove = useCallback(
    (itemId: string | number) => {
      const newSelection = value.filter((v) => v.id !== itemId);
      onChange?.(newSelection);
    },
    [value, onChange],
  );

  // Handle clearing all selections
  const handleClearAll = useCallback(() => {
    onChange?.([]);
    setSearchQuery("");
  }, [onChange]);

  // Handle adding a new item
  const handleAddNew = useCallback(() => {
    if (searchQuery.trim().length > 0) {
      onAddNew?.(searchQuery.trim());
      setSearchQuery("");
      if (!multiple) {
        setOpen(false);
      }
    }
  }, [searchQuery, onAddNew, multiple]);

  // Handle input change
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
      setKeyboardIndex(null);
    },
    [],
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (keyboardIndex === null) {
          setKeyboardIndex(visibleIndices[0] ?? null);
        } else {
          const currentIdx = visibleIndices.indexOf(keyboardIndex);
          if (currentIdx < visibleIndices.length - 1) {
            const nextIdx = visibleIndices[currentIdx + 1];
            setKeyboardIndex(nextIdx);
            itemRefs.current[nextIdx]?.scrollIntoView({ block: "nearest" });
          }
        }
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        if (keyboardIndex !== null) {
          const currentIdx = visibleIndices.indexOf(keyboardIndex);
          if (currentIdx > 0) {
            const prevIdx = visibleIndices[currentIdx - 1];
            setKeyboardIndex(prevIdx);
            itemRefs.current[prevIdx]?.scrollIntoView({ block: "nearest" });
          }
        }
      }

      if (e.key === "Enter") {
        e.preventDefault();
        if (keyboardIndex !== null) {
          handleSelect(sortedItems[keyboardIndex]);
        } else if (filteredItems.length === 1) {
          handleSelect(filteredItems[0]);
        } else if (
          allowAddNew &&
          searchQuery.trim().length > 0 &&
          filteredItems.length === 0
        ) {
          handleAddNew();
        }
      }

      if (e.key === "Escape") {
        inputRef.current?.blur();
      }
    },
    [
      keyboardIndex,
      visibleIndices,
      sortedItems,
      filteredItems,
      handleSelect,
      allowAddNew,
      searchQuery,
      handleAddNew,
    ],
  );

  // Handle arrow click to toggle dropdown
  const handleArrowClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (open) {
        inputRef.current?.blur();
      } else {
        inputRef.current?.focus();
      }
    },
    [open],
  );

  // Sync search query with selected value for single select
  // When value changes externally (including being cleared), update the display
  useEffect(() => {
    if (!multiple && !open) {
      if (value.length > 0) {
        setSearchQuery(value[0].label);
      } else {
        // Value was cleared externally - clear the search query too
        setSearchQuery("");
      }
    }
  }, [multiple, value, open]);

  return (
    <div
      className={classNames(
        styles.container,
        { [styles.clean]: isClean },
        className,
      )}
      data-testid={testId}
      data-size={size}
    >
      {/* Label */}
      {label && (
        <label
          htmlFor={fieldId}
          className={classNames(styles.label, {
            [styles.dark]: dark,
            [styles.clean]: isClean,
          })}
          data-mode={dark ? "dark" : "light"}
        >
          {label}
        </label>
      )}

      {/* Multi-select chips */}
      {multiple && value.length > 0 && (
        <>
          {/* Use ChipGroup when chipGroupMax is set and we exceed the max */}
          {chipGroupMax !== undefined && value.length > chipGroupMax ? (
            <div
              className={classNames(styles.chipRow, {
                [styles.clean]: isClean,
              })}
            >
              {value.length > 1 && (
                <IconButton
                  icon={<Icon name="x" size="md" aria-hidden />}
                  variant="clear"
                  size="sm"
                  onClick={handleClearAll}
                  aria-label="Clear all selections"
                  className={styles.clearSelection}
                  data-testid={testId ? `${testId}-clear-all` : undefined}
                />
              )}
              <ChipGroup
                items={value.map((sel) => ({
                  id: sel.id,
                  label: sel.label,
                }))}
                max={chipGroupMax}
                size={size === "lg" ? "lg" : size === "sm" ? "sm" : "md"}
                panelTitle={chipGroupTitle}
                onRemove={(item) => handleRemove(item.id)}
                data-testid={testId ? `${testId}-chip-group` : undefined}
              />
            </div>
          ) : (
            <div
              className={classNames(styles.chipRow, {
                [styles.clean]: isClean,
              })}
              role="list"
              aria-label="Selected items"
            >
              {value.length > 1 && (
                <IconButton
                  icon={<Icon name="x" size="md" aria-hidden />}
                  variant="clear"
                  size="sm"
                  onClick={handleClearAll}
                  aria-label="Clear all selections"
                  className={styles.clearSelection}
                  data-testid={testId ? `${testId}-clear-all` : undefined}
                />
              )}
              {value.map((sel) => (
                <div key={sel.id} className={styles.chip} role="listitem">
                  <span className={styles.chipLabel}>{sel.label}</span>
                  <IconButton
                    icon={<Icon name="x" size="sm" aria-hidden />}
                    variant="clear"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(sel.id);
                    }}
                    aria-label={`Remove ${sel.label}`}
                    className={styles.chipRemove}
                    data-testid={
                      testId ? `${testId}-chip-${sel.id}` : undefined
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Dropdown container */}
      <div
        className={classNames(styles.dropdown, {
          [styles.dark]: dark,
          [styles.clean]: isClean,
          [styles.highlight]: open,
          [styles.error]: !!error,
          [styles.disabled]: disabled,
        })}
      >
        {/* Input and arrow container */}
        <div className={styles.innerContent}>
          {/* Custom value display overlay - shows when closed with selection and renderValue provided */}
          {!open && !multiple && value.length > 0 && renderValue && (
            <div
              className={styles.customValueOverlay}
              onClick={() => inputRef.current?.focus()}
            >
              {renderValue(value[0])}
            </div>
          )}
          <input
            ref={inputRef}
            id={fieldId}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-controls={`${fieldId}-listbox`}
            aria-invalid={!!error}
            aria-describedby={error ? `${fieldId}-error` : undefined}
            className={classNames(styles.input, {
              [styles.hiddenInput]:
                !open && !multiple && value.length > 0 && renderValue,
            })}
            value={displayText}
            placeholder={placeholder}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            readOnly={!searchable}
            disabled={disabled}
            data-testid={testId ? `${testId}-input` : undefined}
          />
          <div
            className={classNames(styles.arrow, { [styles.open]: open })}
            onMouseDown={handleArrowClick}
          >
            <Icon name="chevronDown" size="xl" aria-hidden={true} />
          </div>
        </div>

        {/* Dropdown list container */}
        <div className={styles.drawerContainer}>
          {open && (
            <div
              ref={listRef}
              id={`${fieldId}-listbox`}
              role="listbox"
              aria-label={label || "Options"}
              className={classNames(styles.drawer, { [styles.clean]: isClean })}
              data-testid={testId ? `${testId}-options` : undefined}
            >
              {/* Options */}
              {sortedItems.map((item, idx) => {
                const isHidden = !filteredItems.includes(item);
                const isFocused = keyboardIndex === idx;
                const isSelected = value.some((v) => v.id === item.id);

                if (isHidden) return null;

                // Use renderOption for custom rendering
                const optionState = {
                  selected: isSelected,
                  focused: isFocused,
                  disabled: !!item.disabled,
                };

                const customContent = renderOption?.(item, optionState);

                return (
                  <div
                    key={item.id}
                    ref={(el) => {
                      itemRefs.current[idx] = el;
                    }}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={item.disabled}
                    className={classNames(styles.drawerListItem, {
                      [styles.focused]: isFocused,
                      [styles.disabled]: item.disabled,
                      [styles.customOption]: !!renderOption,
                    })}
                    onMouseDown={() => handleSelect(item)}
                    data-testid={
                      testId ? `${testId}-option-${item.id}` : undefined
                    }
                  >
                    {customContent ?? (
                      <div className={styles.drawerListName}>{item.label}</div>
                    )}
                  </div>
                );
              })}

              {/* No results message */}
              {filteredItems.length === 0 && !allowAddNew && (
                <div className={styles.noResults}>Ingen resultater</div>
              )}

              {/* Add new option - only show when no results */}
              {allowAddNew &&
                searchQuery.trim().length > 0 &&
                filteredItems.length === 0 && (
                  <div
                    className={classNames(styles.drawerListItem, styles.addNew)}
                    onMouseDown={handleAddNew}
                    data-testid={testId ? `${testId}-add-new` : undefined}
                  >
                    <div className={styles.drawerListName}>
                      Legg til "{searchQuery}"
                    </div>
                  </div>
                )}
            </div>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <span
          id={`${fieldId}-error`}
          className={styles.errorMessage}
          role="alert"
          aria-live="assertive"
        >
          {error}
        </span>
      )}
    </div>
  );
};

Select.displayName = "Select";

export default Select;
